/**
 * Team/TeamGallery.jsx
 * ════════════════════════════════════════════════════════════════
 * Публічна сторінка "Команда" — режим галереї фото.
 * Сітка портретів + модалка з origin animation (як Apple Photos).
 *
 * Props:
 *   members     {array}   — список членів команди
 *   description {string}  — вступний текст
 *   descOpen    {bool}
 *   onDescToggle {fn}
 *   cols        {number}  — кількість колонок, default 5
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronLeft, faChevronRight, faXmark,
    faChevronDown, faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { constr_uri } from '../../../../../services/apiClient';

// ── Стилі ────────────────────────────────────────────────────────────────────

var STYLES_INJECTED = false;
function injectStyles() {
    if (STYLES_INJECTED) return;
    STYLES_INJECTED = true;
    var s = document.createElement('style');
    s.textContent = [
        /* Оверлей */
        '.tg-overlay { position:fixed; inset:0; z-index:9999; background:rgba(0,0,0,0); transition:background 0.35s ease; pointer-events:none; }',
        '.tg-overlay.tg-visible { background:rgba(0,0,0,0.85); pointer-events:all; }',
        /* Модалка */
        '.tg-modal { position:fixed; z-index:10000; border-radius:14px; overflow:hidden; background:#fff; box-shadow:0 24px 80px rgba(0,0,0,0.5); will-change:transform,opacity; transition:transform 0.38s cubic-bezier(0.34,1.2,0.64,1), opacity 0.32s ease, border-radius 0.38s ease; }',
        /* Мобільна модалка — повний екран знизу */
        '@media (max-width:639px) { .tg-modal { border-radius:20px 20px 0 0 !important; } }',
        /* Фото в сітці */
        '.tg-thumb { cursor:pointer; overflow:hidden; border-radius:10px; transition:transform 0.18s ease, box-shadow 0.18s ease; }',
        '.tg-thumb:hover { transform:scale(1.04); box-shadow:0 6px 20px rgba(0,0,0,0.2); }',
        '.tg-thumb img { width:100%; height:100%; object-fit:cover; object-position:top center; display:block; transition:transform 0.38s ease; }',
        /* Навігаційні кнопки — fixed відносно viewport, зовні модалки */
        '.tg-nav { position:fixed; top:50%; transform:translateY(-50%); width:48px; height:48px; border-radius:50%; background:rgba(255,255,255,0.1); border:none; cursor:pointer; color:rgba(255,255,255,0.85); display:flex; align-items:center; justify-content:center; font-size:20px; transition:background 0.15s, color 0.15s; z-index:10001; }',
        '.tg-nav:hover { background:rgba(255,255,255,0.2); color:#fff; }',
        /* Спінер */
        '@keyframes tg-spin { to { transform:rotate(360deg); } }',
        '.tg-spinner { width:28px; height:28px; border:3px solid rgba(0,0,0,0.1); border-top-color:#0069ff; border-radius:50%; animation:tg-spin 0.7s linear infinite; }',
    ].join('\n');
    document.head.appendChild(s);
}

// ── Модалка ───────────────────────────────────────────────────────────────────

var GROUP_TAGS = {
    1: { label: 'Керівництво',            bg: '#e6f1fb', color: '#0c447c' },
    2: { label: 'Вихователі та педагоги', bg: '#e1f5ee', color: '#085041' },
    3: { label: 'Інші',                   bg: '#f1efe8', color: '#444441' },
};

var MemberModal = function(props) {
    var members    = props.members;
    var startIndex = props.startIndex;
    var originRect = props.originRect;
    var onClose    = props.onClose;

    var [current, setCurrent]     = useState(startIndex);
    var [visible, setVisible]     = useState(false);
    var [imgLoaded, setImgLoaded] = useState(false);
    var [swipeDelta, setSwipeDelta] = useState(0); // px зміщення при свайпі
    var modalRef   = useRef(null);
    var touchStart = useRef(null);
    var isMobile   = window.innerWidth < 640;

    // MemberModal може використовуватись і без батьківського TeamGallery
    // (напр. з картки вихователя в групі) — тому інжектимо стилі тут теж,
    // а не покладаємось лише на useEffect у TeamGallery. injectStyles()
    // сама себе захищає від повторного виконання (STYLES_INJECTED).
    useEffect(function() { injectStyles(); }, []);

    var m = members[current];
    var avatarSrc = m && m.photoDocId && m.avatarFileId
        ? constr_uri('docfile?doc_id=' + m.photoDocId + '&file_id=' + m.avatarFileId)
        : null;

    // ── Розміри модалки залежно від розміру екрану ───────────────────────────

    var getModalSize = function() {
        var vw = window.innerWidth;
        var vh = window.innerHeight;
        if (isMobile) {
            return {
                mw: vw,
                mh: vh,
                mx: 0,
                my: 0,
                borderRadius: '20px 20px 0 0',
            };
        }
        var mw = Math.min(720, vw - 32);
        var mh = Math.min(560, vh - 40);
        return {
            mw: mw,
            mh: mh,
            mx: (vw - mw) / 2,
            my: (vh - mh) / 2,
            borderRadius: '14px',
        };
    };

    // ── Відкриття з origin ────────────────────────────────────────────────────

    useEffect(function() {
        if (!modalRef.current) return;

        var s  = getModalSize();
        var el = modalRef.current;

        el.style.left         = s.mx + 'px';
        el.style.top          = isMobile ? 'auto' : s.my + 'px';
        el.style.bottom       = isMobile ? '0' : 'auto';
        el.style.width        = s.mw + 'px';
        el.style.height       = s.mh + 'px';
        el.style.borderRadius = s.borderRadius;

        if (originRect) {
            var scaleX = originRect.width  / s.mw;
            var scaleY = originRect.height / s.mh;
            var scale  = Math.max(scaleX, scaleY) * 0.85;
            var ox = originRect.left + originRect.width  / 2 - (s.mx + s.mw / 2);
            var oy = originRect.top  + originRect.height / 2 - (s.my + s.mh / 2);
            el.style.transform = 'translate(' + ox + 'px,' + oy + 'px) scale(' + scale + ')';
            el.style.opacity   = '0';
        } else {
            el.style.transform = isMobile ? 'translateY(100%)' : 'scale(0.92)';
            el.style.opacity   = '0';
        }

        el.getBoundingClientRect();

        requestAnimationFrame(function() {
            el.style.transform    = 'translate(0,0) scale(1)';
            el.style.opacity      = '1';
            setVisible(true);
        });

        document.body.style.overflow = 'hidden';
        return function() { document.body.style.overflow = ''; };
    }, []);

    // ── Закриття ─────────────────────────────────────────────────────────────

    var handleClose = function() {
        if (!modalRef.current) { onClose(); return; }
        var el = modalRef.current;

        if (isMobile) {
            el.style.transition = 'transform 0.28s cubic-bezier(0.4,0,1,1), opacity 0.22s ease';
            el.style.transform  = 'translateY(100%)';
            el.style.opacity    = '0.5';
        } else if (originRect) {
            var s = getModalSize();
            var scaleX = originRect.width  / s.mw;
            var scaleY = originRect.height / s.mh;
            var scale  = Math.max(scaleX, scaleY) * 0.85;
            var ox = originRect.left + originRect.width  / 2 - (s.mx + s.mw / 2);
            var oy = originRect.top  + originRect.height / 2 - (s.my + s.mh / 2);
            el.style.transition   = 'transform 0.28s cubic-bezier(0.4,0,1,1), opacity 0.22s ease, border-radius 0.28s ease';
            el.style.transform    = 'translate(' + ox + 'px,' + oy + 'px) scale(' + scale + ')';
            el.style.opacity      = '0';
            el.style.borderRadius = '10px';
        } else {
            el.style.transition = 'transform 0.25s ease, opacity 0.2s ease';
            el.style.transform  = 'scale(0.92)';
            el.style.opacity    = '0';
        }

        setTimeout(onClose, 300);
    };

    // ── Клавіатура ────────────────────────────────────────────────────────────

    var goPrev = function() { setImgLoaded(false); setCurrent(function(c) { return c > 0 ? c - 1 : members.length - 1; }); };
    var goNext = function() { setImgLoaded(false); setCurrent(function(c) { return c < members.length - 1 ? c + 1 : 0; }); };

    useEffect(function() {
        var handler = function(e) {
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'ArrowLeft')  goPrev();
            if (e.key === 'Escape')     handleClose();
        };
        document.addEventListener('keydown', handler);
        return function() { document.removeEventListener('keydown', handler); };
    }, [current]);

    // ── Touch / Swipe ─────────────────────────────────────────────────────────

    var handleTouchStart = function(e) {
        touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now() };
        setSwipeDelta(0);
    };

    var handleTouchMove = function(e) {
        if (!touchStart.current) return;
        var dy = e.touches[0].clientY - touchStart.current.y;
        var dx = e.touches[0].clientX - touchStart.current.x;

        if (isMobile) {
            // Вертикальний свайп — закриття
            if (dy > 0 && Math.abs(dy) > Math.abs(dx)) {
                e.preventDefault();
                setSwipeDelta(dy);
            }
        } else {
            // Горизонтальний свайп — навігація (десктоп з тачпадом)
            if (Math.abs(dx) > Math.abs(dy)) {
                setSwipeDelta(dx);
            }
        }
    };

    var handleTouchEnd = function(e) {
        if (!touchStart.current) return;
        var dy  = e.changedTouches[0].clientY - touchStart.current.y;
        var dx  = e.changedTouches[0].clientX - touchStart.current.x;
        var dt  = Date.now() - touchStart.current.time;
        var fast = dt < 300;

        if (isMobile) {
            // Свайп вниз — закрити
            if (dy > 120 || (fast && dy > 50)) {
                handleClose();
            } else {
                setSwipeDelta(0);
            }
        } else {
            // Свайп вліво — наступний, вправо — попередній
            if (dx < -80 || (fast && dx < -40)) goNext();
            else if (dx > 80 || (fast && dx > 40)) goPrev();
            else setSwipeDelta(0);
        }
        touchStart.current = null;
    };

    // ── Transform при свайпі ──────────────────────────────────────────────────

    var swipeStyle = {};
    if (swipeDelta !== 0) {
        if (isMobile) {
            swipeStyle = {
                transform: 'translateY(' + Math.max(0, swipeDelta) + 'px)',
                transition: 'none',
                opacity: 1 - Math.max(0, swipeDelta) / 400,
            };
        }
    }

    if (!m) return null;
    var tag = GROUP_TAGS[m.displayGroup] || GROUP_TAGS[3];

    return (
        <>
            {/* Оверлей */}
            <div
                className={'tg-overlay' + (visible ? ' tg-visible' : '')}
                onClick={handleClose}
            />

            {/* Стрілки десктоп */}
            {!isMobile && visible && members.length > 1 && (
                <>
                    <button className="tg-nav" onClick={function(e){ e.stopPropagation(); goPrev(); }}
                        style={{ left: 'calc(50% - min(360px, calc(50vw - 16px)) - 64px)' }}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button className="tg-nav" onClick={function(e){ e.stopPropagation(); goNext(); }}
                        style={{ right: 'calc(50% - min(360px, calc(50vw - 16px)) - 64px)' }}>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </>
            )}

            {/* Модалка */}
            <div
                ref={modalRef}
                className="tg-modal"
                style={swipeStyle}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* Swipe indicator (мобільний) */}
                {isMobile && (
                    <div style={{
                        position: 'absolute', top: 8, left: '50%',
                        transform: 'translateX(-50%)',
                        width: 36, height: 4, borderRadius: 2,
                        background: 'rgba(0,0,0,0.18)', zIndex: 20,
                    }} />
                )}

                {/* Кнопка закрити */}
                <button onClick={handleClose} style={{
                    position: 'absolute', top: 12, right: 12, zIndex: 20,
                    width: 34, height: 34, borderRadius: '50%',
                    background: isMobile ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.45)',
                    border: 'none', cursor: 'pointer',
                    color: isMobile ? '#333' : '#fff', fontSize: 16,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <FontAwesomeIcon icon={faXmark} />
                </button>

                {/* Лічильник */}
                {members.length > 1 && (
                    <div style={{
                        position: 'absolute', top: 16, left: 16, zIndex: 20,
                        fontSize: 12, fontWeight: 600,
                        color: isMobile ? '#6b7280' : '#fff',
                        background: isMobile ? 'transparent' : 'rgba(0,0,0,0.45)',
                        padding: isMobile ? '0' : '4px 10px',
                        borderRadius: 12,
                    }}>
                        {current + 1} / {members.length}
                    </div>
                )}

                {/* ── МОБІЛЬНИЙ LAYOUT ── */}
                {isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                        {/* Фото — верхня частина */}
                        <div style={{
                            flexShrink: 0,
                            height: '42vh',
                            position: 'relative',
                            background: '#f3f4f6',
                        }}>
                            {!imgLoaded && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="tg-spinner" /></div>}
                            {avatarSrc && (
                                <img key={m.id} src={avatarSrc} alt={m.fullName}
                                    onLoad={function() { setImgLoaded(true); }}
                                    style={{
                                        position: 'absolute', inset: 0,
                                        width: '100%', height: '100%',
                                        objectFit: 'cover', objectPosition: 'top center',
                                        opacity: imgLoaded ? 1 : 0,
                                        transition: 'opacity 0.25s',
                                    }}
                                />
                            )}
                            {/* Градієнт внизу фото */}
                            <div style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0, height: 60,
                                background: 'linear-gradient(transparent, #fff)',
                            }} />
                        </div>

                        {/* Інфо — скролиться */}
                        <div style={{ flex: 1, overflow: 'auto', padding: '4px 20px 32px', WebkitOverflowScrolling: 'touch' }}>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', lineHeight: 1.25, marginBottom: 4 }}>
                                {m.fullName}
                            </div>
                            <div style={{ fontSize: 14, color: '#0069ff', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>{m.position || '—'}</span>
                                {m.age && <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400, borderLeft: '1px solid #e5e7eb', paddingLeft: 8 }}>{m.age} р.</span>}
                            </div>
                            <div style={{ marginBottom: 14 }}>
                                <span style={{ display: 'inline-block', background: tag.bg, color: tag.color, padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                                    {tag.label}
                                </span>
                            </div>
                            {m.bio ? (
                                <div style={{ fontSize: 14, lineHeight: 1.75, color: '#374151' }} dangerouslySetInnerHTML={{ __html: m.bio }} />
                            ) : (
                                <div style={{ color: '#adb5bd', fontSize: 14 }}>Інформація не заповнена</div>
                            )}
                        </div>

                        {/* Свайп підказка */}
                        {members.length > 1 && (
                            <div style={{ textAlign: 'center', padding: '8px 0 20px', fontSize: 11, color: '#adb5bd' }}>
                                ← свайп для навігації →
                            </div>
                        )}
                    </div>

                ) : (

                    /* ── ДЕСКТОП LAYOUT ── */
                    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
                        <div style={{ width: 220, flexShrink: 0, background: '#f3f4f6', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {!imgLoaded && <div className="tg-spinner" />}
                            {avatarSrc && (
                                <img key={m.id} src={avatarSrc} alt={m.fullName}
                                    onLoad={function() { setImgLoaded(true); }}
                                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.25s' }}
                                />
                            )}
                        </div>
                        <div style={{ flex: 1, overflow: 'auto', padding: '28px 28px 20px', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', lineHeight: 1.25, marginBottom: 6, marginTop: 8 }}>
                                {m.fullName}
                            </div>
                            <div style={{ fontSize: 14, color: '#0069ff', fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>{m.position || '—'}</span>
                                {m.age && <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400, borderLeft: '1px solid #e5e7eb', paddingLeft: 8 }}>{m.age} р.</span>}
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <span style={{ display: 'inline-block', background: tag.bg, color: tag.color, padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                                    {tag.label}
                                </span>
                            </div>
                            {m.bio ? (
                                <div style={{ fontSize: 14, lineHeight: 1.75, color: '#374151', flex: 1 }} dangerouslySetInnerHTML={{ __html: m.bio }} />
                            ) : (
                                <div style={{ color: '#adb5bd', fontSize: 14 }}>Інформація не заповнена</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

// ── Сітка фото ────────────────────────────────────────────────────────────────

var TeamGallery = function(props) {
    var members     = props.members     || [];
    var description = props.description || '';
    var descOpen    = props.descOpen    || false;
    var onDescToggle = props.onDescToggle || function(){};
    var cols        = props.cols        || 5;

    var [modal, setModal] = useState(null); // { index, originRect }
    var thumbRefs         = useRef([]);

    useEffect(function() { injectStyles(); }, []);

    // Показуємо тільки тих у кого є фото
    var visible = members.filter(function(m) { return m.photoDocId && m.avatarFileId; });

    var handleThumbClick = function(idx) {
        var el = thumbRefs.current[idx];
        if (!el) return;
        var rect = el.getBoundingClientRect();
        setModal({
            index: idx,
            originRect: {
                top:    rect.top    + window.scrollY,
                left:   rect.left  + window.scrollX,
                width:  rect.width,
                height: rect.height,
            },
        });
    };

    // Виправляємо originRect при відкритті (враховуємо поточний scrollY)
    var getAdjustedRect = function(rect) {
        return {
            top:    rect.top    - window.scrollY,
            left:   rect.left  - window.scrollX,
            width:  rect.width,
            height: rect.height,
        };
    };

    if (visible.length === 0) return null;

    // Перший — директор, більший розмір
    // Підрахуємо розміри через CSS grid з auto-fit
    var thumbSize    = 'calc((100% - ' + (cols - 1) * 8 + 'px) / ' + cols + ')';
    var directorSize = 'calc((100% - ' + (cols - 1) * 8 + 'px) / ' + cols + ' * 1.5 + 8px)';

    return (
        <div>
            {/* ── Сітка фото ── */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
            }}>
                {visible.map(function(m, idx) {
                    var isDirector = idx === 0;
                    var w = isDirector ? directorSize : thumbSize;
                    var h = isDirector ? 'calc(' + w + ' * 1.2)' : 'calc(' + thumbSize + ' * 1.25)';

                    return (
                        <div
                            key={m.id}
                            ref={function(el) { thumbRefs.current[idx] = el; }}
                            className="tg-thumb"
                            onClick={function() { handleThumbClick(idx); }}
                            style={{
                                width: w,
                                height: h,
                                flexShrink: 0,
                                position: 'relative',
                            }}
                        >
                            <img
                                src={constr_uri('docfile?doc_id=' + m.photoDocId + '&file_id=' + m.avatarFileId)}
                                alt={m.fullName}
                                loading="lazy"
                            />
                            {/* Overlay з ім'ям при hover */}
                            <div style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                                padding: '20px 8px 8px',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                            }}
                                onMouseEnter={function(e) { e.currentTarget.style.opacity = '1'; }}
                                onMouseLeave={function(e) { e.currentTarget.style.opacity = '0'; }}
                            >
                                <div style={{ fontSize: isDirector ? 13 : 11, color: '#fff', fontWeight: 600, lineHeight: 1.3 }}>
                                    {m.fullName}
                                </div>
                                <div style={{ fontSize: isDirector ? 11 : 10, color: 'rgba(255,255,255,0.75)' }}>
                                    {m.position}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ── Опис команди ── */}
            {description && (
                <div style={{
                    marginTop: 20,
                    background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f5e9 100%)',
                    borderRadius: 14,
                    padding: '20px 24px',
                    border: '1px solid rgba(0,105,255,0.08)',
                }}>
                    <div style={{
                        fontSize: 13, fontWeight: 700, color: '#6b7280',
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                        marginBottom: 10,
                    }}>
                        Наша команда
                    </div>
                    <div style={{ position: 'relative' }}>
                        <div
                            style={{
                                fontSize: 15, lineHeight: 1.7, color: '#374151',
                                maxHeight: descOpen ? 'none' : 160,
                                overflow: 'hidden',
                                transition: 'max-height 0.3s ease',
                            }}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                        {!descOpen && (
                            <div style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                height: 56,
                                background: 'linear-gradient(transparent, #e8f5e9)',
                                pointerEvents: 'none',
                            }} />
                        )}
                    </div>
                    <div style={{ marginTop: descOpen ? 12 : 4, textAlign: 'center' }}>
                        <button
                            type="button"
                            onClick={onDescToggle}
                            style={{
                                background: 'rgba(255,255,255,0.7)',
                                border: '1px solid rgba(0,105,255,0.2)',
                                borderRadius: 20, padding: '4px 16px',
                                fontSize: 12, color: '#0069ff', fontWeight: 600,
                                cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                            }}
                        >
                            <FontAwesomeIcon icon={descOpen ? faChevronUp : faChevronDown} style={{ fontSize: 10 }} />
                            {descOpen ? 'Згорнути' : 'Читати далі'}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Модалка ── */}
            {modal && (
                <MemberModal
                    members={visible}
                    startIndex={modal.index}
                    originRect={getAdjustedRect(modal.originRect)}
                    onClose={function() { setModal(null); }}
                />
            )}
        </div>
    );
};

export { MemberModal };
export default TeamGallery;